/**
 * LoginPage
 * =========
 *
 * Email + password sign-in via Supabase. On success the
 * ``onAuthStateChange`` listener in ``AuthProvider`` fires, the session
 * goes non-null, and ``ProtectedRoute`` redirects the user back to
 * whatever they originally tried to load (preserved in
 * ``location.state.from``).
 *
 * We keep the UI deliberately plain — branding lives elsewhere in the
 * app; this page is one of three near-identical auth screens (login /
 * signup / reset) and they share the ``AuthShell`` layout.
 */
import { useState, FormEvent } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { supabase } from "../auth/supabase";
import AuthShell from "../components/AuthShell";

export default function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const nav = useNavigate();
  const loc = useLocation();
  const redirectTo = (loc.state as { from?: string } | null)?.from ?? "/uploads";

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    setBusy(false);
    if (error) {
      setError(error.message);
      return;
    }
    nav(redirectTo, { replace: true });
  }

  return (
    <AuthShell title="Sign in to PBMC Platform">
      <form onSubmit={onSubmit} className="space-y-4">
        <div>
          <label className="label">Email</label>
          <input className="input" type="email" autoComplete="email"
                 value={email} onChange={(e) => setEmail(e.target.value)}
                 required />
        </div>
        <div>
          <label className="label">Password</label>
          <input className="input" type="password" autoComplete="current-password"
                 value={password} onChange={(e) => setPassword(e.target.value)}
                 required />
        </div>
        {error && (
          <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">
            {error}
          </div>
        )}
        <button className="btn-primary w-full" disabled={busy} type="submit">
          {busy ? "Signing in…" : "Sign in"}
        </button>
      </form>
      <div className="mt-4 flex items-center justify-between text-xs text-slate-500">
        <Link to="/signup" className="hover:text-accent-600">Create account</Link>
        <Link to="/reset-password" className="hover:text-accent-600">Forgot password?</Link>
      </div>
    </AuthShell>
  );
}
